// Thin TTS wrapper to centralize Web Speech API usage for games
// Exports: getVoices(), onVoicesChanged(cb), speakText(text, opts), speakNumber(num, opts)

let _voices = [];
let _listeners = new Set();

function _loadVoices() {
  if (typeof window === 'undefined' || typeof window.speechSynthesis === 'undefined') return [];
  try {
    _voices = window.speechSynthesis.getVoices() || [];
  } catch (e) {
    _voices = [];
  }
  return _voices;
}

if (typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined') {
  _loadVoices();
  const handler = () => {
    _loadVoices();
    for (const cb of _listeners) {
      try { cb(_voices); } catch (e) {}
    }
  };
  try {
    if (window.speechSynthesis.addEventListener) window.speechSynthesis.addEventListener('voiceschanged', handler);
    else window.speechSynthesis.onvoiceschanged = handler;
  } catch (e) {}
}

export function getVoices() {
  return _voices.slice();
}

export function onVoicesChanged(cb) {
  if (typeof cb !== 'function') return () => {};
  _listeners.add(cb);
  // call immediately with current list
  try { cb(_voices.slice()); } catch (e) {}
  return () => { _listeners.delete(cb); };
}

function _findVoice(avail, voiceURIOrName) {
  if (!avail || avail.length === 0) return null;
  if (!voiceURIOrName) return null;
  return avail.find(v => v.voiceURI === voiceURIOrName || v.name === voiceURIOrName) || null;
}

export function speakText(text, { voiceURI = null, rate = 1.0, cancel = true } = {}) {
  if (typeof window === 'undefined' || typeof window.speechSynthesis === 'undefined') return null;
  try {
    const utter = new SpeechSynthesisUtterance(String(text));
    utter.rate = Number(rate) || 1.0;
    const avail = getVoices();
    const match = _findVoice(avail, voiceURI) || avail.find(v => /en/i.test(v.lang)) || avail[0];
    if (match) utter.voice = match;
    if (cancel) window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
    return utter;
  } catch (e) { return null; }
}

export function speakNumber(num, { voiceURI = null, rate = 1.0, callDescriptions = null } = {}) {
  if (num == null) return null;
  if (typeof window === 'undefined' || typeof window.speechSynthesis === 'undefined') return null;
  try {
    const desc = (callDescriptions && Array.isArray(callDescriptions) && num >= 1 && num <= callDescriptions.length) ? callDescriptions[num - 1] : null;
    const avail = getVoices();
    // speak description then number
    if (desc) {
      const utterDesc = new SpeechSynthesisUtterance(desc + '.');
      utterDesc.rate = Number(rate) || 1.0;
      const match = _findVoice(avail, voiceURI) || avail.find(v => /en/i.test(v.lang)) || avail[0];
      if (match) utterDesc.voice = match;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterDesc);
      setTimeout(() => {
        try {
          const utterNum = new SpeechSynthesisUtterance(String(num));
          utterNum.rate = Number(rate) || 1.0;
          const match2 = _findVoice(getVoices(), voiceURI) || getVoices().find(v => /en/i.test(v.lang)) || getVoices()[0];
          if (match2) utterNum.voice = match2;
          window.speechSynthesis.speak(utterNum);
        } catch (e) {}
      }, 700);
      return true;
    }
    // no description, speak number only
    const utter = new SpeechSynthesisUtterance(String(num));
    utter.rate = Number(rate) || 1.0;
    const match = _findVoice(avail, voiceURI) || avail.find(v => /en/i.test(v.lang)) || avail[0];
    if (match) utter.voice = match;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
    return true;
  } catch (e) { return null; }
}

const tts = {
  getVoices,
  onVoicesChanged,
  speakText,
  speakNumber,
};

export default tts;
